/* eslint-disable react/no-array-index-key */
import React, {
  useCallback, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import useDebounce from '../../../../hooks/debounce';
import RequirementChip from '../create-position-requirement-chip/create-position-requirement-chip.component';
import STRING_KEYS from '../../../../language/keys';
import { THEME } from '../../../../themes';
import { Input } from '../../../onboarding/components/input/input.component';
import { PressEnterMessage } from '../press-enter-message/press-enter-message.component';
import {
  HoriDotsIcon,
  ListStartIcon,
  InputContainer,
  ContentContainer,
  SuggestionContainer,
  SuggestionContentContainer,
  SuggestionListContainer,
  SuggestionTitle,
  SuggestionTitleContainer,
  InputWrapper,
} from './create-position-requirements.styled';
import {
  OnboardingJobRequirementComponentProps,
} from './create-position-requirements';

const SKELETON_LIST = [
  'first', 'second', 'third',
];

const CreatePositionRequirementsComponent = ({
  suggestionsJobRequirements,
  setSuggestionsJobRequirements,
  customJobRequirements,
  setCustomJobRequirements,
  isSuggestionsJobRequirementsLoading,
  placeholder,
} : OnboardingJobRequirementComponentProps) : JSX.Element => {
  const [input, setInput] = useState('');
  const [isInputFocused, setInputFocused] = useState<boolean>(false);
  const debouncedIsSuggestionsJobRequirementsLoading = useDebounce<boolean>(isSuggestionsJobRequirementsLoading, 500);
  const { t: translate } = useTranslation();

  const renderSuggestionListItems = () => {
    if (debouncedIsSuggestionsJobRequirementsLoading) {
      return (
        <>
          { SKELETON_LIST.map((key) => (
            <SuggestionListContainer key={ key } >
              <Skeleton
                variant="rectangular"
                width={ 150 }
                height={ 40 }
                sx={ {
                  borderRadius: THEME.RADIUS.BASE,
                } }
              />
            </SuggestionListContainer>
          )) }
        </>
      );
    }

    return suggestionsJobRequirements?.map(({ title, id, selected }) => (
      <SuggestionListContainer
        key={ id }
      >
        <RequirementChip
          title={ title }
          selected={ selected }
          onClick={ () => {
            const newJobRequirements = suggestionsJobRequirements.map((jobRequirement) => ({
              ...jobRequirement,
              selected: jobRequirement.title.toLowerCase() === title.toLowerCase() ? !jobRequirement.selected : jobRequirement.selected,
            }));
            setSuggestionsJobRequirements(newJobRequirements);
          } }
        />
      </SuggestionListContainer>
    ));
  };

  const renderCustomListItems = () => (
    customJobRequirements?.map(({ title, selected }, index) => (
      <SuggestionListContainer
        key={ `${title}_${index}` }
      >
        <RequirementChip
          title={ title }
          selected={ selected }
          onClick={ () => {
            const newJobRequirements = customJobRequirements.filter((jobRequirement) => jobRequirement.title.toLowerCase() !== title.toLowerCase());
            setCustomJobRequirements?.(newJobRequirements);
          } }
        />
      </SuggestionListContainer>
    ))
  );

  const onClickEnter = useCallback(() => {
    if (!input) {
      return;
    }

    const newItem : PositionJobRequirementState = {
      selected: true,
      title: input,
    };

    setCustomJobRequirements?.([...(customJobRequirements || []), newItem]);
    setInput('');
  }, [input, customJobRequirements, setCustomJobRequirements]);

  const handleInputFocus = () => {
    setInputFocused(true);
  };

  return (
    <>
      <SuggestionContainer>
        <SuggestionTitleContainer>
          <ListStartIcon />
          <SuggestionTitle>
            { translate(STRING_KEYS.ONBOARDING.ONBOARDING_SUGGESTIONS_JOB_REQUIREMENTS_TITLE) }
          </SuggestionTitle>
        </SuggestionTitleContainer>
        <SuggestionContentContainer>
          { renderSuggestionListItems() }
        </SuggestionContentContainer>
        <SuggestionTitleContainer>
          <HoriDotsIcon />
          <SuggestionTitle>
            { translate(STRING_KEYS.ONBOARDING.ONBOARDING_CUSTOM_JOB_REQUIREMENTS_TITLE) }
          </SuggestionTitle>
        </SuggestionTitleContainer>
        <ContentContainer>
          { renderCustomListItems() }
          <InputContainer>
            <InputWrapper>
              <Input
                autoFocus
                placeholder={ translate(placeholder) }
                value={ input }
                onValueChange={ (value) => {
                  if (value.endsWith(',')) {
                    onClickEnter();
                  } else {
                    setInput(value);
                  }
                } }
                onFocus={ handleInputFocus }
                onEnter={ onClickEnter }
                isInputFocused={ isInputFocused }
              />
            </InputWrapper>
            <PressEnterMessage translationKey={ STRING_KEYS.PRESS_ENTER } />
          </InputContainer>
        </ContentContainer>
      </SuggestionContainer>
    </>
  );
};

export default CreatePositionRequirementsComponent;

CreatePositionRequirementsComponent.defaultProps = {
  enableFreeText: false,
};
